/**
 * `openDocsAt` — Function F2 from the help-docs-panel design.
 *
 * Imperative entry point that any part of the app can call to open the docs
 * slider at a specific path (and optionally a heading anchor). It never
 * touches React state directly; instead it fires a `CustomEvent` on `window`
 * that {@link ./DocsSliderProvider | DocsSliderProvider} subscribes to. The
 * URL is also updated non-destructively so the state is deep-linkable and
 * reload-safe.
 *
 * @remarks
 * Contract summary (see design.md § Function 2 — `openDocsAt(path, anchor?)`):
 *
 * - `path` MUST be a non-empty string ending in `.md`; otherwise a
 *   `TypeError` is thrown.
 * - `anchor`, when defined, MUST match `/^[a-z0-9-]+$/`; otherwise a
 *   `TypeError` is thrown. The pattern is the same slug shape enforced by
 *   `parseDeepLink.ts`, `rehype-slug`, and the docs manifest generator.
 * - A `CustomEvent("help-docs-panel:open", { detail: { path, anchor } })`
 *   fires on `window` exactly once per successful call.
 * - The URL is updated via `history.replaceState` (never `pushState`) so
 *   the browser's back-button behaviour is unchanged. Existing query
 *   parameters other than `docs` are preserved.
 * - In SSR / non-browser environments (no `window`, no `history`, or no
 *   `CustomEvent` constructor) the call silently no-ops. It never throws
 *   for environment reasons — only for precondition violations.
 *
 * @see .kiro/specs/help-docs-panel/design.md — Function 2 `openDocsAt(path, anchor?)`
 * @see .kiro/specs/help-docs-panel/requirements.md — Requirements 1.1, 3.4, 8.6
 * @see ./parseDeepLink.ts — the reverse operation (URL → state)
 *
 * @packageDocumentation
 */

/** The event type the DocsSliderProvider subscribes to. */
const OPEN_EVENT_TYPE = "help-docs-panel:open";

/**
 * Slug pattern accepted for anchors. Mirrors:
 *  - `parseDeepLink.ts` (Req 1.8)
 *  - `rehype-slug` GitHub-flavoured slug shape
 *  - the manifest generator's heading slugs (Req 5.7)
 */
const ANCHOR_PATTERN = /^[a-z0-9-]+$/;

/** Detail payload for the `help-docs-panel:open` CustomEvent. */
export interface OpenDocsAtEventDetail {
  readonly path: string;
  readonly anchor?: string;
}

/**
 * Open the docs slider at `path` (optionally scrolled to `anchor`).
 *
 * @param path - A docs-relative path ending in `.md`, e.g. `"api/statements.md"`.
 * @param anchor - Optional heading slug matching `/^[a-z0-9-]+$/`,
 *   e.g. `"collection-rate"`.
 *
 * @throws {TypeError} When `path` is not a non-empty `.md` string, or when
 *   `anchor` is defined but does not match `/^[a-z0-9-]+$/`.
 *
 * @example
 * ```ts
 * import { openDocsAt } from "@/help-docs-panel";
 *
 * // From anywhere in the app:
 * openDocsAt("api/statements.md", "collection-rate");
 * ```
 */
export function openDocsAt(path: string, anchor?: string): void {
  // Precondition: path must be a non-empty string ending in ".md".
  if (typeof path !== "string" || path.length === 0 || !path.endsWith(".md")) {
    throw new TypeError(
      `openDocsAt: path must be a non-empty string ending in ".md" (got ${JSON.stringify(path)})`,
    );
  }

  // Precondition: anchor, when defined, must be a lowercase slug.
  if (anchor !== undefined) {
    if (typeof anchor !== "string" || !ANCHOR_PATTERN.test(anchor)) {
      throw new TypeError(
        `openDocsAt: anchor must match /^[a-z0-9-]+$/ (got ${JSON.stringify(anchor)})`,
      );
    }
  }

  // SSR-safe: silently no-op when we're not in a browser. The precondition
  // checks above still run in Node so callers get the same error signal
  // whether they run under jsdom, a browser, or plain Node.
  if (typeof window === "undefined") return;
  if (typeof window.history === "undefined") return;
  if (typeof window.CustomEvent !== "function") return;

  // 1) Fire the event so subscribers (DocsSliderProvider) can react.
  //    We construct a fresh detail object rather than aliasing arguments so
  //    the payload is not accidentally mutated by a subscriber that also
  //    happens to hold a reference to the input object.
  const detail: OpenDocsAtEventDetail =
    anchor === undefined ? { path } : { path, anchor };
  const event = new window.CustomEvent<OpenDocsAtEventDetail>(OPEN_EVENT_TYPE, {
    detail,
  });
  window.dispatchEvent(event);

  // 2) Update the URL non-destructively. We use URLSearchParams so any
  //    other query parameters that happen to be on the URL survive. The
  //    hash is written verbatim (no URL-encoding — anchors are ASCII slugs
  //    per ANCHOR_PATTERN, so nothing needs escaping).
  try {
    const params = new URLSearchParams(window.location.search);
    params.set("docs", path);
    const queryString = params.toString();
    const search = queryString.length > 0 ? `?${queryString}` : "";
    const hash = anchor === undefined ? "" : `#${anchor}`;
    // Empty string would clear pathname; explicit fallback keeps it stable.
    const newUrl = `${search}${hash}` || window.location.pathname;
    window.history.replaceState({}, "", newUrl);
  } catch {
    // history.replaceState can throw a SecurityError in exotic sandboxed
    // environments (cross-origin iframes, sandboxed test hosts). The event
    // has already fired, so the panel will still open — the URL just won't
    // be deep-linkable this time. Never let this crash the caller.
  }
}
